import { useState, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { EASE } from "@/lib/motion";

export const FounderGallerySlider = ({ items = [] }) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const reduceMotion = useReducedMotion();

    const count = items.length;

    // Auto-advance every 5 seconds
    useEffect(() => {
        if (reduceMotion || isPaused || count <= 1) return undefined;
        const timer = setInterval(() => {
            setActiveIndex((prev) => (prev + 1) % count);
        }, 5000);
        return () => clearInterval(timer);
    }, [reduceMotion, isPaused, count]);

    if (!items || items.length === 0) return null;

    return (
        <div
            className="relative w-full py-4 overflow-hidden select-none"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onTouchStart={() => setIsPaused(true)}
            onTouchEnd={() => setIsPaused(false)}
        >
            {/* Carousel Display Track: Exactly 3 visible at any time */}
            <div className="relative mx-auto flex h-[440px] sm:h-[500px] md:h-[550px] w-full max-w-[1240px] items-center justify-center">
                {items.map((item, index) => {
                    // Calculate circular relative offset (-count/2 ... +count/2)
                    let offset = index - activeIndex;
                    if (offset < -count / 2) offset += count;
                    if (offset > count / 2) offset -= count;

                    const isCenter = offset === 0;
                    const isLeft = offset === -1;
                    const isRight = offset === 1;
                    const isVisible = isCenter || isLeft || isRight;

                    // Responsive spacing to create slight elegant overlap behind center card
                    const baseSpacing =
                        typeof window !== "undefined"
                            ? window.innerWidth < 640
                                ? 175
                                : window.innerWidth < 1024
                                ? 270
                                : 340
                            : 340;

                    const xPos = isCenter ? 0 : isLeft ? -baseSpacing : isRight ? baseSpacing : offset * (baseSpacing * 1.5);
                    const scale = isCenter ? 1.06 : isVisible ? 0.84 : 0.6;
                    const opacity = isCenter ? 1 : isVisible ? 0.65 : 0;
                    const zIndex = isCenter ? 30 : isVisible ? 10 : 0;

                    return (
                        <motion.div
                            key={item.id || index}
                            className={`absolute aspect-[4/5] w-[240px] sm:w-[310px] md:w-[370px] overflow-hidden rounded-[22px] md:rounded-[28px] bg-white border border-black/5 ${
                                isVisible ? "cursor-pointer" : "pointer-events-none"
                            }`}
                            style={{
                                transformOrigin: "center center",
                            }}
                            animate={{
                                x: xPos,
                                scale,
                                opacity,
                                zIndex,
                                boxShadow: isCenter
                                    ? "0 26px 52px -12px rgba(0, 0, 0, 0.28)"
                                    : "0 10px 24px -8px rgba(0, 0, 0, 0.12)",
                            }}
                            transition={{
                                duration: 0.8,
                                ease: EASE,
                            }}
                            onClick={() => {
                                if (isVisible) setActiveIndex(index);
                            }}
                        >
                            <img
                                src={item.image}
                                alt={`André Andreetta ${index + 1}`}
                                className="h-full w-full object-cover pointer-events-none select-none"
                                loading="lazy"
                                draggable="false"
                            />
                        </motion.div>
                    );
                })}
            </div>

            {/* Navigation Dots Indicator */}
            <div className="mt-4 flex justify-center items-center gap-2.5">
                {items.map((_, idx) => {
                    const isActive = idx === activeIndex;
                    return (
                        <button
                            key={idx}
                            type="button"
                            onClick={() => setActiveIndex(idx)}
                            aria-label={`Ir para foto ${idx + 1}`}
                            className={`h-2.5 rounded-full transition-all duration-300 ${
                                isActive
                                    ? "w-8 bg-brand"
                                    : "w-2.5 bg-black/20 hover:bg-black/40"
                            }`}
                        />
                    );
                })}
            </div>
        </div>
    );
};
